import { DownloadOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Button, message, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { WhitelistUploadRecord } from 'Modules'
import { useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { downloadBlob } from 'utils/download-file'
import { formatDate } from 'utils/format-time'
import { logError } from 'utils/logger'

export const PTO_WHITELIST_HISTORY_QUERY_KEY = 'pto-whitelist-history'

const WhitelistHistory = ({ id }: { id?: string | null }) => {
  const [page, setPage] = useState(DEFAULT_PAGE_INDEX)
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)
  const [exportingId, setExportingId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: [PTO_WHITELIST_HISTORY_QUERY_KEY, id, page, limit],
    queryFn: async () => {
      const res = await AppAdmin.getPTOWhitelistHistory(id as string, { page, limit })
      return res.data?.data
    },
    enabled: !!id,
    gcTime: 30000,
  })

  const handleExport = async (record: WhitelistUploadRecord) => {
    if (!id || exportingId) return
    setExportingId(record._id)
    try {
      const res = await AppAdmin.exportPTOWhitelistUpload(id, record._id)
      downloadBlob(
        res.data,
        `whitelist_${record._id}.xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      )
    } catch (error) {
      logError('Export whitelist failed', error)
      message.error('Failed to export whitelist')
    } finally {
      setExportingId(null)
    }
  }

  const columns: ColumnsType<WhitelistUploadRecord> = [
    {
      title: 'Uploaded At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) => (value ? formatDate(value) : '-'),
    },
    {
      title: 'Uploaded By',
      dataIndex: 'uploadedBy',
      key: 'uploadedBy',
      render: (value: string) => value || '-',
    },
    {
      title: 'Total Wallets',
      dataIndex: 'totalCount',
      key: 'totalCount',
      render: (value: number) => new Intl.NumberFormat('en-US').format(value ?? 0),
    },
    {
      title: 'Action',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<DownloadOutlined />}
          loading={exportingId === record._id}
          onClick={() => handleExport(record)}
        >
          Download
        </Button>
      ),
    },
  ]

  if (!id) return null

  return (
    <div style={{ marginTop: 16 }}>
      <p className="text-12" style={{ marginBottom: 8 }}>
        Uploaded whitelist files
      </p>
      <Table
        rowKey="_id"
        size="small"
        columns={columns}
        dataSource={data?.results ?? []}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: limit,
          total: data?.totalResults ?? 0,
          showSizeChanger: false,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage)
            setLimit(nextSize)
          },
        }}
      />
    </div>
  )
}

export default WhitelistHistory
